import _ from "lodash";

export const imageRoot = "/img/products/";
const unknown = "unknown_device";
const unknownImage = `${imageRoot}${unknown}.png`;
export const STUDIO_USB = "Studio USB";
export const EAGLE_EYE_CUBE = "EagleEye Cube";

export const unknownImageUrl = () => {
  return `${imageRoot}${unknown}.png`;
};

type ValueTester = (
  low: number,
  high: number,
  value: number
) => boolean | string;
type ImageGetter = (val: number) => string | undefined;
interface Thener {
  then: (l: number, h: number, v: string) => Thener;
  build: ImageGetter;
}
export const lookupChain = (checker: ValueTester): Thener => {
  const lookup = (l: number, h: number, v: string) => (
    test: number
  ): string | undefined => {
    if (checker(l, h, test)) return v;
    return undefined;
  };
  const checkers: Array<ImageGetter> = [];
  const check: ImageGetter = (value: number) => {
    return checkers.map(checker => checker(value)).find(v => v);
  };
  const then = (l: number, h: number, v: string) => {
    checkers.push(lookup(l, h, v));
    return {
      then: then,
      build: check
    };
  };
  return {
    then: then,
    build: check
  };
};

/**
 * Check if a number falls within the range
 * @param low Low end, inclusive
 * @param high High end, exclusive
 * @param test The value to test
 * @returns {boolean} Was it in the range
 */
const rangeCheck = (low: number, high: number, test: number) =>
  test >= low && test < high;

const VVXLookup = lookupChain(rangeCheck)
  .then(100, 150, `${imageRoot}vvx_101.png`)
  .then(150, 200, `${imageRoot}vvx_150.png`)
  .then(200, 250, `${imageRoot}vvx_201.png`)
  .then(250, 300, `${imageRoot}vvx_250.png`)
  .then(300, 301, `${imageRoot}vvx_300.png`)
  .then(301, 350, `${imageRoot}vvx_301.png`)
  .then(350, 400, `${imageRoot}vvx_350.png`)
  .then(400, 401, `${imageRoot}vvx_400.png`)
  .then(401, 411, `${imageRoot}vvx_401.png`)
  .then(411, 450, `${imageRoot}vvx_411.png`)
  .then(450, 500, `${imageRoot}vvx_450.png`)
  .then(500, 501, `${imageRoot}vvx_500.png`)
  .then(501, 600, `${imageRoot}vvx_501.png`)
  .then(600, 601, `${imageRoot}vvx_600.png`)
  .then(601, 700, `${imageRoot}vvx_601.png`).build;

const TrioLookup = lookupChain(rangeCheck)
  .then(60, 61, `${imageRoot}trio_c60.png`)
  .then(8000, 8500, `${imageRoot}trio_8300.png`)
  .then(8500, 8800, `${imageRoot}trio_8500.png`)
  .then(8800, 10000, `${imageRoot}trio_8800.png`).build;

const GSLookup = lookupChain(rangeCheck)
  .then(0, 7500, `${imageRoot}${unknown}.png`)
  .then(7500, 10000, `${imageRoot}g7500.png`).build;

const StudioLookup = lookupChain(rangeCheck)
  .then(0, 50, `${imageRoot}x30.png`)
  .then(50, 100, `${imageRoot}x50.png`).build;

const CCXLookup = lookupChain(rangeCheck)
  .then(400, 500, `${imageRoot}ccx_400.png`)
  .then(500, 600, `${imageRoot}ccx_500.png`)
  .then(600, 700, `${imageRoot}ccx_600.png`)
  .then(700, 800, `${imageRoot}ccx_700.png`).build;

const OtherModelsLookup = (model: string) => {
  if (model === "HENDRIX" || model === "RICHARDS" || model === "WILLIE") {
    return `${imageRoot}g7500.png`;
  } else if (model === STUDIO_USB) {
    return `${imageRoot}studio.png`;
  } else if (model === EAGLE_EYE_CUBE) {
    return `${imageRoot}eagle_eye_cube.png`;
  }
};

type ImageLookupMap = Record<string, (val: number) => string | undefined>;
/**
 * A map of lookups for phone images.
 * Keyed by phone model/type eg:VVX.
 * Each key returns a function that takes the model number and returns either the image url or undefined.
 * @type {{VVX: *}}
 */
const HardwareModelImageMap: ImageLookupMap = {
  VVX: VVXLookup,
  Trio: TrioLookup,
  G: GSLookup,
  Studio: StudioLookup,
  StudioX: StudioLookup,
  CCX: CCXLookup
};

const OtherModels = [
  "HENDRIX",
  "RICHARDS",
  "WILLIE",
  STUDIO_USB,
  EAGLE_EYE_CUBE
];

type ModelInformation = {
  model?: string;
  no: number;
};
/*
 * Extract the model number from the hardware
 * 'VVX 501' => {type: 'VVX', no: 501}
 * 'VVX cat' => {type: 'VVX', no: NaN}
 * 'VVX' => {type: 'VVX', no: NaN}
 * '' => {type: 'unknown', no: NaN}
 *
 */
export const extractModelInformation = (
  hardwareModel: string | null | undefined
): ModelInformation => {
  const getSingleMatch = (res: RegExpMatchArray | null) => {
    return res?.length === 1 ? res[0] : null;
  };
  let model = unknown;
  let no = NaN;

  if (hardwareModel) {
    // "Studio USB" and "EagleEye Cube" aren't really suitable to below extract logic
    // They don't contain number, so return information will be { model: "Studio"/"EagleEye", no: NaN }
    // Which isn't really helpful so it will be handled differently
    if (hardwareModel === STUDIO_USB || hardwareModel === EAGLE_EYE_CUBE) {
      model = hardwareModel;
    } else {
      //Match the first number
      const match = hardwareModel.match(/\d+/);
      no = getSingleMatch(match) ? parseInt(getSingleMatch(match)!, 10) : NaN;
      //Match up to a number, whitespace, or the end of the string non inclusive
      const matchModel = hardwareModel.match(/^[^(\d|\s)]+/);
      model = getSingleMatch(matchModel)
        ? getSingleMatch(matchModel)!
        : unknown;
    }
  }

  return {
    model: model,
    no: no
  };
};

export const findClosestImageMatch = (modelInfo: ModelInformation): string => {
  const { model, no } = modelInfo;
  if (
    !model ||
    model === unknown ||
    (!_.has(HardwareModelImageMap, model) && !_.includes(OtherModels, model))
  ) {
    return unknownImage;
  }

  let potentialImage;
  if (_.includes(OtherModels, model)) {
    potentialImage = OtherModelsLookup(model);
  } else if (!_.isNaN(no)) {
    let imageList = HardwareModelImageMap[model];
    potentialImage = imageList(no);
  }

  if (!potentialImage) {
    return unknownImage;
  }

  return potentialImage;
};

/**
 * Attempts to find an image for a given model string.
 * @param hardwareModel the model string, eg: 'VVX 500'
 * @returns {string} either the url to the image, either the actual model image or an unknown placeholder.
 * @constructor
 */
export const lookupModelImage = (hardwareModel: string | null | undefined) =>
  findClosestImageMatch(extractModelInformation(hardwareModel));

export const getCatalogImages = () => {};
